/**
 * @file
 * The shared tooltip bubble (berTags.tooltip).
 */

((Drupal) => {
  const { berTags } = Drupal;
  const { el, textSpan } = berTags.dom;

  // Tooltip: one shared, light bubble for rows and tags

  const tooltip = {
    node: null,
    target: null,
    suspended: false,

    // While suspended (a drag in progress) open() is a no-op, so moving
    // over other items mid-drag cannot pop bubbles. resume() re-arms.
    suspend() {
      this.suspended = true;
      this.hide();
    },

    resume() {
      this.suspended = false;
    },

    hide() {
      if (this.node) {
        this.node.classList.remove('is-open');
      }
      if (this.target) {
        this.target.removeAttribute('aria-describedby');
        this.target = null;
      }
    },

    // The parts list controls which details show (#tooltip_parts on the
    // element; the widgets pass a fixed all-parts list, each part renders
    // only when the option carries it).
    show(target, data, labelText, parts, opts = {}) {
      const lines = [];
      if (parts.includes('hierarchy') && data.parents) {
        // Crumb spans, CSS draws the forward icon between them.
        const path = el('span', 'ber-tip__path');
        [...data.parents.split(' › '), labelText].forEach((segment) => {
          path.appendChild(textSpan('ber-tip__crumb', segment));
        });
        lines.push(path);
      }
      // Type, id and status make up one compact fact line.
      const facts = [];
      if (parts.includes('type') && (data.type || data.bundle)) {
        facts.push(data.type || data.bundle);
      }
      if (parts.includes('id') && data.id) {
        facts.push(Drupal.t('ID: @id', { '@id': data.id }));
      }
      if (parts.includes('status') && data.status) {
        facts.push(data.status);
      }
      if (facts.length) {
        lines.push(['ber-tip__id', facts.join(' · ')]);
      }
      if (parts.includes('description') && data.description) {
        // Long descriptions get clamped so the tooltip stays a tooltip.
        const description =
          data.description.length > 160
            ? `${data.description.slice(0, 159).trimEnd()}…`
            : data.description;
        lines.push(['ber-tip__text', description]);
      }
      this.open(target, lines, opts);
    },

    // opts.align: 'start' (default - bubble start-aligned to the target,
    // caret at its side inset), 'end' (end-aligned, caret flips to the
    // end side), 'center' (bubble centered, caret in its middle), or
    // 'button' (the kit buttons' mode: start-side caret shifted to point
    // at the target's center). The attachers and withTooltip expose it
    // as start/end/center/auto, auto resolving to their own default.
    open(target, lines, opts = {}) {
      if (this.suspended || !lines.length) {
        return;
      }
      if (!this.node) {
        this.node = el('div', 'ber-tip');
        this.node.id = 'ber-tip';
        this.node.setAttribute('role', 'tooltip');
        document.body.appendChild(this.node);
      }
      this.node.textContent = '';
      lines.forEach((line) => {
        this.node.appendChild(
          line instanceof Element ? line : textSpan(line[0], line[1]),
        );
      });
      this.node.classList.add('is-open');
      this.opts = opts;
      this.place(target, opts);
      this.target = target;
      target.setAttribute('aria-describedby', 'ber-tip');
    },

    // Coordinates only, so an open bubble can be re-placed without being
    // rebuilt. Every class it may add is cleared first: it runs repeatedly.
    place(target, opts = {}) {
      this.node.classList.remove(
        'ber-tip--below',
        'ber-tip--end',
        'ber-tip--center',
      );
      const rect = target.getBoundingClientRect();
      const width = this.node.offsetWidth;
      let start = rect.left;
      if (opts.align === 'end') {
        start = rect.right - width;
        this.node.classList.add('ber-tip--end');
      } else if (opts.align === 'center') {
        start = rect.left + rect.width / 2 - width / 2;
        this.node.classList.add('ber-tip--center');
      } else if (opts.align === 'button') {
        // The caret sits at a fixed inset from the bubble's side (see
        // .ber-tip::after); shift the bubble so the caret's center lands
        // on the target's center. Clamping can override at the edges.
        const rootStyle = getComputedStyle(document.documentElement);
        const scale =
          parseFloat(rootStyle.getPropertyValue('--ber-scale')) || 1;
        start =
          rect.left +
          rect.width / 2 -
          (0.9 * parseFloat(rootStyle.fontSize) + 5) * scale;
      }
      const left = Math.max(
        8,
        Math.min(start, document.documentElement.clientWidth - width - 8),
      );
      // Above the target with the caret pointing down, below as a fallback.
      let top = rect.top - this.node.offsetHeight - 8;
      if (top < 8) {
        top = rect.bottom + 8;
        this.node.classList.add('ber-tip--below');
      }
      this.node.style.left = `${left}px`;
      this.node.style.top = `${top}px`;
    },

    // Re-places an open bubble against its own target. The popover machinery
    // calls this whenever a balloon moves (picking an item grows the tags
    // row, which shifts the "+" and the whole balloon under the pointer) -
    // the bubble is fixed-positioned, so without it it stays at the
    // coordinates it opened at. A target that the re-render replaced is
    // gone from the DOM: drop the bubble rather than point it at nothing.
    reposition() {
      if (
        !this.target ||
        !this.node ||
        !this.node.classList.contains('is-open')
      ) {
        return;
      }
      if (!this.target.isConnected) {
        this.hide();
        return;
      }
      this.place(this.target, this.opts || {});
    },

    // start/end/center pass through to open(); auto (the default) keeps
    // the attachers' start alignment.
    resolveAlign(align) {
      return align && align !== 'auto' ? align : undefined;
    },

    // Delegated hover and focus tooltips for the selector inside container.
    attach(container, { selector, data, label, parts, align }) {
      const opts = { align: this.resolveAlign(align) };
      const over = (event) => {
        const target = event.target.closest(selector);
        if (target && !target.contains(event.relatedTarget)) {
          this.show(target, data(target), label(target), parts, opts);
        }
      };
      const out = (event) => {
        const target = event.target.closest(selector);
        if (target && !target.contains(event.relatedTarget)) {
          this.hide();
        }
      };
      container.addEventListener('mouseover', over);
      container.addEventListener('mouseout', out);
      container.addEventListener('focusin', (event) => {
        const target = event.target.closest(selector);
        if (target) {
          this.show(target, data(target), label(target), parts, opts);
        }
      });
      container.addEventListener('focusout', () => this.hide());
    },

    // Plain text bubbles for control buttons. Replaces the native title
    // tooltip and refreshes on click, labels change when a button toggles.
    attachText(container, { selector, text, align }) {
      const opts = { align: this.resolveAlign(align) };
      const showFor = (target) => {
        target.removeAttribute('title');
        this.open(target, [['ber-tip__text', text(target)]], opts);
      };
      container.addEventListener('mouseover', (event) => {
        const target = event.target.closest(selector);
        if (target && !target.contains(event.relatedTarget)) {
          showFor(target);
        }
      });
      container.addEventListener('mouseout', (event) => {
        const target = event.target.closest(selector);
        if (target && !target.contains(event.relatedTarget)) {
          this.hide();
        }
      });
      container.addEventListener('focusin', (event) => {
        const target = event.target.closest(selector);
        if (target) {
          showFor(target);
        }
      });
      container.addEventListener('focusout', () => this.hide());
      // Capture phase, the buttons stop propagation. Deferred so the
      // clicked button has updated its label first.
      container.addEventListener(
        'click',
        (event) => {
          const target = event.target.closest(selector);
          if (target && this.target === target) {
            setTimeout(() => showFor(target));
          }
        },
        true,
      );
    },

    // Delegated tooltips whose lines the caller builds per target.
    attachLines(container, { selector, lines, skip, align }) {
      const opts = { align: this.resolveAlign(align) };
      container.addEventListener('mouseover', (event) => {
        const target = event.target.closest(selector);
        if (!target || (skip && skip(target))) {
          return;
        }
        if (!target.contains(event.relatedTarget)) {
          this.open(target, lines(target), opts);
        }
      });
      container.addEventListener('mouseout', (event) => {
        const target = event.target.closest(selector);
        if (target && !target.contains(event.relatedTarget)) {
          this.hide();
        }
      });
      container.addEventListener('focusin', (event) => {
        const target = event.target.closest(selector);
        if (target && target === event.target && !(skip && skip(target))) {
          this.open(target, lines(target), opts);
        }
      });
      container.addEventListener('focusout', () => this.hide());
    },
  };

  berTags.tooltip = tooltip;

  // A balloon that re-places drags its rows with it, so a bubble anchored
  // to one has to follow. The popover announces the move and the tooltip
  // decides what to do about it, rather than the popover calling in here.
  // Registered at load, so it runs once per page like the file itself.
  document.addEventListener('ber-popover:place', () => tooltip.reposition());
})(Drupal);
